// KPI Builder — a KPI built part by part. Every part a hand-written query has is editable: each query's index, date field,
// filter path and extra top-level keys, any aggregation of any type (nested), and the dashboard filters.
import { INDEX_PREFIX } from 'constants/kpiBuilderCatalogue';
import { stripEnvPrefix } from './custom';
import { AdvancedAnswers, AdvancedQuery, AggNode, BuilderAnswers, EsNode, ExtraSetting, QueryObject } from './types';

export const blankQuery = (): AdvancedQuery => ({ index: '', date: '', filterPath: '', top: '{"size":0}', map: [], aggs: [] });
export const blankNode = (): AggNode => ({ name: '', type: '', body: '{}', children: [] });

/** Starting settings for the common aggregation types, so a new aggregation is never an empty box. */
export const AGG_TEMPLATES: Record<string, string> = {
  terms: '{"field": "", "size": 10}',
  filter: '{"terms": {"field": []}}',
  filters: '{"filters": {}}',
  range: '{"field": "", "ranges": []}',
  cardinality: '{"field": "userId"}',
  value_count: '{"field": "userId"}',
  avg: '{"field": ""}',
  sum: '{"field": ""}',
  min: '{"field": ""}',
  max: '{"field": ""}',
  bucket_script: '{"buckets_path": {}, "script": ""}',
  bucket_sort: '{"sort": [], "size": 10}',
  avg_bucket: '{"buckets_path": ""}',
  scripted_metric: '{"init_script": "", "map_script": "", "combine_script": "", "reduce_script": ""}',
};
export const AGG_TYPES = Object.keys(AGG_TEMPLATES);

type Parsed = { ok: true; value: unknown } | { ok: false };
const parse = (text: string): Parsed => {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch {
    return { ok: false };
  }
};
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

const bodyOf = (n: AggNode): unknown => {
  const p = parse(n.body.trim() === '' ? '{}' : n.body);
  return p.ok ? p.value : {};
};

/** One aggregation as Elasticsearch reads it: its type with its settings, and the aggregations inside it. */
export const nodeJson = (n: AggNode): EsNode => ({ [n.type.trim()]: bodyOf(n), ...(n.children.length ? { aggs: childrenJson(n.children) } : {}) });
export const childrenJson = (nodes: AggNode[]): Record<string, EsNode> => Object.fromEntries(nodes.map((n) => [n.name.trim(), nodeJson(n)]));

const nodeProblems = (n: AggNode, where: string): string[] => {
  const label = `${where}${n.name.trim() || '(unnamed)'}`;
  const own: string[] = [];
  if (!n.name.trim()) own.push(`${label}: give the aggregation a name.`);
  if (!n.type.trim()) own.push(`${label}: choose a type.`);
  if (!parse(n.body.trim() === '' ? '{}' : n.body).ok) own.push(`${label}: the settings are not valid JSON.`);
  const names = n.children.map((c) => c.name.trim()).filter(Boolean);
  if (new Set(names).size !== names.length) own.push(`${label}: two aggregations inside it share a name.`);
  return [...own, ...n.children.flatMap((c) => nodeProblems(c, `${label} > `))];
};

const queryProblems = (q: AdvancedQuery, i: number): string[] => {
  const where = `Query ${i + 1}`;
  const out: string[] = [];
  if (!q.index.trim()) out.push(`${where}: type the index name.`);
  if (!q.date.trim()) out.push(`${where}: type the date field.`);
  const top = parse(q.top.trim() === '' ? '{}' : q.top);
  if (!top.ok || !isObject(top.value)) out.push(`${where}: the top-level keys are not a valid JSON object.`);
  if (!q.aggs.length) out.push(`${where}: add at least one aggregation.`);
  const names = q.aggs.map((n) => n.name.trim()).filter(Boolean);
  if (new Set(names).size !== names.length) out.push(`${where}: two aggregations share a name.`);
  return [...out, ...q.aggs.flatMap((n) => nodeProblems(n, `${where} > `))];
};

/** Everything that stops this KPI from being generated, in plain words. */
export const advancedProblems = (adv: AdvancedAnswers): string[] =>
  (adv.queries.length ? adv.queries.flatMap(queryProblems) : ['Add at least one query.']);

export const isAdvancedReady = (adv: AdvancedAnswers): boolean => advancedProblems(adv).length === 0;

/** The stored queries. Call only when the answers are ready. */
export function buildAdvanced(a: BuilderAnswers): QueryObject[] {
  return a.advanced.queries.map((q) => {
    const top = parse(q.top.trim() === '' ? '{}' : q.top);
    const map = Object.fromEntries(q.map.filter((f) => f.key.trim() && f.field.trim()).map((f) => [f.key.trim(), f.field.trim()]));
    return {
      module: 'COMMON', dataSource: 'ES', requestQueryMap: map, dateRefField: q.date.trim(), indexName: INDEX_PREFIX + stripEnvPrefix(q.index), esInstance: 'QUADNARY',
      ...(q.filterPath.trim() ? { filterPath: q.filterPath.trim() } : {}),
      aggrQuery: { ...(top.ok && isObject(top.value) ? top.value : {}), aggs: childrenJson(q.aggs) },
    } as QueryObject;
  });
}

// ---------- reading a stored KPI back into parts ----------

/** The aggregations inside an aggs block, or null when one is not a plain "type plus aggs" shape. */
export function nodesFromJson(aggs: unknown): AggNode[] | null {
  if (!isObject(aggs)) return null;
  const nodes: AggNode[] = [];
  for (const [name, raw] of Object.entries(aggs)) {
    if (!isObject(raw)) return null;
    const types = Object.keys(raw).filter((k) => k !== 'aggs');
    if (types.length !== 1) return null;
    const children = raw.aggs === undefined ? [] : nodesFromJson(raw.aggs);
    if (!children) return null;
    nodes.push({ name, type: types[0], body: JSON.stringify(raw[types[0]]), children });
  }
  return nodes;
}

const asObject = (v: unknown): Record<string, unknown> | null => {
  const p = typeof v === 'string' ? parse(v) : { ok: true as const, value: v };
  return p.ok && isObject(p.value) ? p.value : null;
};

/** The aggregations of a stored aggrQuery, and every other key beside them, or null when they cannot be shown as parts. */
function splitAggrQuery(aq: Record<string, unknown>): { aggs: AggNode[]; top: Record<string, unknown> } | null {
  const { aggs, ...top } = aq;
  const nodes = aggs === undefined ? [] : nodesFromJson(aggs);
  return nodes ? { aggs: nodes, top } : null;
}

function queryFromStored(raw: unknown): AdvancedQuery | null {
  const q = asObject(raw);
  const aq = asObject(q?.aggrQuery);
  const map = asObject(q?.requestQueryMap ?? {});
  const split = aq && splitAggrQuery(aq);
  if (!q || !map || !split) return null;
  return {
    index: stripEnvPrefix(String(q.indexName ?? '')), date: String(q.dateRefField ?? ''), filterPath: String(q.filterPath ?? ''), top: JSON.stringify(split.top),
    map: Object.entries(map).map(([key, field]) => ({ key, field: String(field), on: '' })), aggs: split.aggs,
  };
}

/** Setting values are typed as JSON; text that would be read as JSON is quoted so it comes back as the same text. */
const settingText = (v: unknown): string => (typeof v === 'string' && parse(v).ok ? JSON.stringify(v) : typeof v === 'string' ? v : JSON.stringify(v));

export const settingsFromConfig = (config: Record<string, unknown>): ExtraSetting[] =>
  Object.entries(config).filter(([key]) => key !== 'builder').map(([key, value]) => ({ key, value: settingText(value) }));

/** A stored KPI as parts, or null when something in it cannot be shown as parts (the JSON editor still can). */
export function importAdvanced(queryText: string, configText: string, handlerKey = ''): Pick<BuilderAnswers, 'advanced' | 'extras'> | null {
  const list = parse(queryText);
  const config = asObject(configText.trim() === '' ? '{}' : configText);
  if (!list.ok || !Array.isArray(list.value) || !config) return null;
  const queries = list.value.map(queryFromStored);
  if (!queries.length || queries.some((q) => q === null)) return null;
  return { advanced: { queries: queries as AdvancedQuery[] }, extras: { handlerKey, filters: [], settings: settingsFromConfig(config) } };
}
